import { useDispatch, useSelector } from 'react-redux';
import { upperCase } from 'lodash';
import UserAccount, { IUserAccount } from '../models/UserAccount';
import { getInsurerFromState, getUserAccount, getUserProfile, isAuthenticated, SIGN_OUT, UPDATE_ACCOUNT, UPDATE_INSURER, UPDATE_PROFILE, USER_ID } from '../states/slices/userAccountSlice';
import { SIGN_IN } from '../resources/links';
import { IUser, User } from '../models/User';
import { useNavigate } from 'react-router-dom';
import { Insurer } from '../models/insurer/Insurer';
import { IInsurer } from '../models/insurer/IInsurer';
import { SET_TERMS_AND_CONDITIONS_DISMISSED, REDIRECT_URL, RESET_GLOBALS } from '../states/slices/globalSlice';
import { getPathAndParamsStringFromUrl } from '../utils/miscFunctions';
import { signoutUser } from '../api/requests';

function useUserAccount() {
  const dispatch = useDispatch();
  const PROFILE = useSelector(getUserProfile);
  const ACCOUNT = useSelector(getUserAccount);
  const IS_AUTHENTICATED = useSelector(isAuthenticated);
  const INSURER = useSelector(getInsurerFromState);
  const nameInitials = upperCase(PROFILE?.firstName?.charAt(0) + PROFILE?.lastName?.charAt(0)).trim();

  const navigate = useNavigate();

  const setProfile = (data: IUser): IUser => {
    dispatch(UPDATE_PROFILE(data));
    return PROFILE;
  };

  const updateProfile = (data: Partial<IUser>): IUser => {
    dispatch(UPDATE_PROFILE(data));
    return PROFILE;
  };

  const getProfile = (): IUser => {
    return PROFILE;
  };

  const getUser = (): User => {
    return new User({
      ...PROFILE
    });
  };

  const setAccount = (data: IUserAccount): IUserAccount => {
    dispatch(UPDATE_ACCOUNT(data));
    return ACCOUNT;
  };

  const updateAccount = (data: Partial<IUserAccount>): IUserAccount => {
    dispatch(UPDATE_ACCOUNT(data));
    return ACCOUNT;
  };

  const updateInsurerInState = (data: Partial<IInsurer>): IInsurer => {
    dispatch(UPDATE_INSURER(data));
    return INSURER;
  };

  const getAccount = () => {
    return new UserAccount(ACCOUNT);
  };

  const getInsurer = (): Insurer => {
    return new Insurer({
      ...INSURER
    });
  };

  const signOut = async (redirect = true) => {
    // Capture current state before any operations
    const redirectUrl = getPathAndParamsStringFromUrl(window.location.toString()) ?? '';
    const accessRight = ACCOUNT.accessRight;
    
    // Create a cleanup function to ensure consistent state
    const cleanupClientState = () => {
      // Dispatch all state resets atomically
      dispatch(SIGN_OUT());
      dispatch(RESET_GLOBALS());
      dispatch(REDIRECT_URL(redirectUrl));
      dispatch(SET_TERMS_AND_CONDITIONS_DISMISSED(false));
    };

    try {
      // Step 1: Call server-side signout first
      if (accessRight) {
        await signoutUser(accessRight);
      }
      
      // Step 2: Only after successful server signout, clean up client state
      cleanupClientState();

      // Step 3: Navigate after state is fully cleaned up
      if (redirect) {
        setTimeout(() => {
          navigate(SIGN_IN, { replace: true });
        }, 0);
      }
    } catch (error) {
      console.error('Signout error:', error);
      
      // Even on error, clean up client state to ensure user is logged out locally
      cleanupClientState();
      
      // Additional cleanup for error scenario
      dispatch(USER_ID(''));

      // Navigate after error cleanup
      if (redirect) {
        setTimeout(() => {
          navigate(SIGN_IN, { replace: true });
        }, 0);
      }
    }
  };

  const user = new User(getProfile());

  return {
    setProfile,
    getProfile,
    getUser,
    updateProfile,
    signOut,
    getAccount,
    updateAccount,
    setAccount,
    IS_AUTHENTICATED,
    nameInitials,
    updateInsurerInState,
    getInsurer,
    user
  };
}

export default useUserAccount;
